import { Button } from "@/components/ui/UIElements/Button";
import React, { useState, useMemo } from "react";
import { PageHeader } from "@finitless/shared/components";
import { useTranslation } from "react-i18next";
import { useThirdPartyIntegrations } from "../../../hooks";
import {
  POSCard,
  DeliverectCard,
  ItsaCheckmateCard,
  StreamOrdersIntegrationCard,
} from "../../ui/POS";
import { IntegrationFilters, FeatureGate } from "../../ui";
import { SUBSCRIPTION_FEATURES } from "../../../lib/subscriptionFeatures";
import "./integrations-ordering.css";

const POSPage = ({ selectedBrand }) => {
  const { t } = useTranslation("integrations");
  const { data, isLoading, error } = useThirdPartyIntegrations();
  const [searchTerm, setSearchTerm] = useState("");

  // Filter only POS integrations
  const posIntegrations = useMemo(() => {
    if (!data?.integrations) return [];
    /**
     * In the future, remove the filter for stream orders integration
     */
    return data.integrations.filter(
      (integration) =>
        integration.type === "pos" &&
        integration.key !== "stream_orders_integration",
    );
  }, [data?.integrations]);

  // Filter POS integrations locally based on search term
  const filteredPOSIntegrations = useMemo(() => {
    if (!searchTerm.trim()) {
      return posIntegrations;
    }

    const lowerSearchTerm = searchTerm.toLowerCase().trim();
    return posIntegrations.filter((integration) =>
      integration.name?.toLowerCase().includes(lowerSearchTerm),
    );
  }, [posIntegrations, searchTerm]);

  // Special POS cards configuration
  const SPECIAL_POS_CARDS = [
    {
      id: "deliverect",
      component: DeliverectCard,
      searchTerms: ["deliverect"],
    },
    {
      id: "itsacheckmate",
      component: ItsaCheckmateCard,
      searchTerms: ["itsacheckmate", "checkmate", "itsa checkmate"],
    },
    {
      id: "stream_orders_integration",
      component: StreamOrdersIntegrationCard,
      searchTerms: ["stream orders integration", "stream orders", "stream"],
    },
  ];

  // Filter special cards based on search term
  const visibleSpecialCards = useMemo(() => {
    if (!searchTerm.trim()) return SPECIAL_POS_CARDS;

    const lowerSearchTerm = searchTerm.toLowerCase().trim();
    return SPECIAL_POS_CARDS.filter((card) =>
      card.searchTerms.some((term) =>
        term.toLowerCase().includes(lowerSearchTerm),
      ),
    );
  }, [searchTerm]);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  return (
    <FeatureGate
      featureKey={SUBSCRIPTION_FEATURES.INTEGRATIONS_POS}
      featureName={t("pos.header.title")}
    >
      <div className="ordering-integrations-page">
        <PageHeader
          title={t("pos.header.title")}
          subtitle={t("pos.header.subtitle")}
          selectedBrand={selectedBrand}
        />

        {/* Filters */}
        {!isLoading && !error && (
          <div className="ordering-integrations-filters">
            <IntegrationFilters
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              placeholder={t("pos.searchPlaceholder")}
            />
          </div>
        )}

        <div className="mt-6">
          {isLoading ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  className="ordering-integration-state border-border bg-background animate-pulse rounded-lg border p-6"
                >
                  <div className="bg-card mb-3 h-12 w-12 rounded-lg"></div>
                  <div className="bg-card mb-2 h-6 w-3/4 rounded"></div>
                  <div className="bg-card mb-4 h-4 w-1/2 rounded"></div>
                  <div className="mb-4 space-y-2">
                    <div className="bg-card h-3 rounded"></div>
                    <div className="bg-card h-3 rounded"></div>
                    <div className="bg-card h-3 rounded"></div>
                  </div>
                  <div className="bg-card h-10 rounded"></div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="ordering-integration-state border-destructive/30 bg-destructive/20 rounded-lg border p-6">
              <div className="flex items-center space-x-3">
                <div className="text-destructive">
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-destructive text-lg font-medium">
                    {t("pos.error.title")}
                  </h3>
                  <p className="text-destructive mt-1 text-sm">
                    {error.message || t("pos.error.defaultMessage")}
                  </p>
                  <Button
                    emphasis="solid"
                    intent="danger"
                    size="touch"
                    onClick={() => window.location.reload()}
                    className="mt-4 px-4 py-2 transition-colors"
                  >
                    {t("actions.retry")}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Show message when search returns no results */}
              {filteredPOSIntegrations.length === 0 &&
                visibleSpecialCards.length === 0 &&
                searchTerm && (
                  <div className="ordering-integration-state mb-6 py-8 text-center">
                    <p className="text-muted-foreground">
                      {t("pos.empty.search", { searchTerm })}
                    </p>
                  </div>
                )}

              <div className="ordering-integration-grid grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                {filteredPOSIntegrations.map((integration) => (
                  <POSCard key={integration.id} integration={integration} />
                ))}
                {visibleSpecialCards.map((card) => {
                  const CardComponent = card.component;
                  return <CardComponent key={card.id} />;
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </FeatureGate>
  );
};

export default POSPage;
